/**
 * QuickActionChips Component
 *
 * Displays a list of quick action chips that users can click to start conversations.
 * Shows first 4 chips by default with a "+ more" button to reveal the rest.
 *
 * @package @ideoz/ui
 */

import React from 'react';
import { Badge } from '../atoms/badge';
import { Icon } from '@ideoz/ui';
import { MinusCircle, PlusCircle } from 'lucide-react';

export interface QuickAction {
  id: string;
  label: string;
  icon: string;
  prompt: string;
}

/**
 * Default quick actions
 */
const DEFAULT_QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'interview-questions',
    label: ' Interview Questions ',
    icon: '🧠',
    prompt: '🧠  Interview Questions ',
  },
  {
    id: '  Proto-persona',
    label: '  Proto-persona',
    icon: '👤',
    prompt: '👤   Proto-persona',
  },
  {
    id: 'how-might-we',
    label: '  How Might We ',
    icon: '🧱',
    prompt: '🧱   How Might We ',
  },
  {
    id: 'survey-questions',
    label: ' Survey questions ',
    icon: '⁉',
    prompt: '⁉  Survey questions ',
  },
  {
    id: 'user-stories',
    label: '  5 Whys',
    icon: '💬',
    prompt: '💬  5 Whys',
  },
  {
    id: 'user-journey-map',
    label: '  User Journey map',
    icon: '🤔 ',
    prompt: '🤔   User Journey map',
  },
  {
    id: 'mind-map',
    label: '  Problem Statement',
    icon: '💡',
    prompt: '💡   Problem Statement',
  },

  {
    id: ' User stories ',
    label: ' User stories ',
    icon: '✨',
    prompt: '✨  User stories ',
  },
  {
    id: ' User Flow Chart',
    label: ' User Flow Chart',
    icon: '⨁',
    prompt: '⨁  User Flow Chart',
  },
  {
    id: '  Mind Map',
    label: '  Mind Map',
    icon: '💡',
    prompt: '💡  Mind Map',
  },
];

export interface QuickActionChipsProps {
  /**
   * List of quick actions to display
   * @default DEFAULT_QUICK_ACTIONS
   */
  actions?: QuickAction[];

  /**
   * Callback when a chip is clicked
   */
  onActionClick: (action: QuickAction) => void;

  /**
   * Number of chips to show initially
   * @default 4
   */
  initialVisibleCount?: number;

  /**
   * Whether to use web or plugin styling
   */
  isWeb?: boolean;

  /**
   * Optional test ID for testing
   */
  testId?: string;

  /**
   * Whether the chips are disabled (e.g., when user is out of credits)
   */
  disabled?: boolean;
}

/**
 * QuickActionChips Component
 */
export const QuickActionChips: React.FC<QuickActionChipsProps> = ({
  actions = DEFAULT_QUICK_ACTIONS,
  onActionClick,
  initialVisibleCount = 4,
  isWeb = false,
  testId = 'quick-action-chips',
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Determine which actions to show
  const visibleActions = isExpanded ? actions : actions.slice(0, initialVisibleCount);
  const hasMoreActions = actions.length > initialVisibleCount;

  return (
    <div className="flex flex-col items-center gap-3" data-testid={testId}>
      {/* Chips container */}
      <div className="flex flex-wrap justify-center gap-2">
        {visibleActions.map((action) => (
          <Badge
            className={disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
            key={action.id}
            variant="default"
            onClick={disabled ? undefined : () => onActionClick(action)}
            data-testid={`quick-action-chip-${action.id}`}
            data-track="quick_action_chip"
            data-track-action-id={action.id}
          >
            {action.icon} {action.label}
          </Badge>
        ))}

        {/* Show "+ more" button if there are hidden actions */}
        {hasMoreActions && !isExpanded && (
          <Badge
            className={disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
            variant="gray"
            onClick={disabled ? undefined : () => setIsExpanded(true)}
            data-testid="quick-action-more-button"
          >
            <span className="flex items-center gap-1">
              <Icon icon={PlusCircle} size={14} className="stroke-muted-foreground" />
              more
            </span>
          </Badge>
        )}

        {/* Show "- less" button if actions are expanded */}
        {hasMoreActions && isExpanded && (
          <Badge
            className={disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
            variant="gray"
            size="sm"
            onClick={disabled ? undefined : () => setIsExpanded(false)}
            data-testid="quick-action-less-button"
          >
            <span className="flex items-center gap-1">
              <Icon icon={MinusCircle} size={14} className="stroke-muted-foreground" />
              less
            </span>
          </Badge>
        )}
      </div>
    </div>
  );
};

export default QuickActionChips;
